/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, PropsWithChildren, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import Button, { ButtonSize, ButtonVariant } from 'components/UI_kit_3/Button/Button';
import Modal from 'components/UI_kit_3/Modal/Modal';
import classNames from 'classnames';
import Tooltip from 'components/UI_kit_3/Popover/Tooltip/Tooltip';
import FormCheck from 'components/UI_kit_3/FormCheck/FormCheck';
import styles from './ButtonWithConfirm.module.scss';

interface IButtonWithConfirmProps {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
  disabled?: boolean
  header?: string
  description?: string | ReactNode
  Icon?: ReactNode
  confirmTitle?: string
  confirmVariant?: ButtonVariant
  confirmCheckbox?: string
  cancelTitle?: string
  tooltip?: string | false
  onConfirm: (checked?: boolean) => void | Promise<void>
  qaElement: string
}

const ButtonWithConfirm = forwardRef<HTMLDivElement, PropsWithChildren<IButtonWithConfirmProps>>(({
  children,
  variant = 'primary',
  size,
  className,
  disabled,
  header,
  description,
  Icon,
  confirmTitle,
  confirmVariant = 'primary',
  confirmCheckbox,
  cancelTitle,
  tooltip,
  onConfirm,
  qaElement,
}, ref) => {
  const [t] = useTranslation();
  const [confirmShow, setConfirmShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [confirmCheck, setConfirmCheck] = React.useState<boolean>(false);

  const handleConfirm = React.useCallback(() => {
    setLoading(true);
    const res = onConfirm(confirmCheck);
    if (res instanceof Promise) {
      res.finally(() => {
        setConfirmShow(false);
        setLoading(false);
      });
    } else {
      setConfirmShow(false);
      setLoading(false);
    }
  }, [onConfirm, confirmCheck]);

  return (
    <div ref={ref} className={className}>
      <Tooltip active={!!tooltip} text={tooltip || ''}>
        <div>
          <Button
            className={styles.btn}
            variant={variant || 'primary'}
            size={size}
            disabled={disabled}
            onClick={() => setConfirmShow(true)}
            qa-element={`${qaElement}-show`}
          >
            {children}
          </Button>
        </div>
      </Tooltip>
      <Modal
        size="sm"
        show={confirmShow}
        dialogClassName={classNames(styles.modal, styles[confirmVariant || ''])}
        onHide={() => setConfirmShow(false)}
      >
        <Modal.Header
          className={classNames({
            'pr-3xl': !!Icon,
          })}
          closeButton
        >
          {Icon && <div className={(styles.headerIcon)}>{Icon}</div>}
          {!Icon && (
          <div>
            {header || t('common.are_you_sure')}
          </div>
          )}
        </Modal.Header>
        {
          (description || (header && Icon) || confirmCheckbox) && (
            <Modal.Body className={styles.description}>
              {Icon && <div className="font-md-semibold">{header || t('common.are_you_sure')}</div>}
              {/* {!description && <p>{t('common.are_you_sure')}</p>} */}
              {typeof description === 'string' && <p>{description}</p>}
              {typeof description === 'object' && description}
              { confirmCheckbox && (
                <FormCheck
                  id="confirm"
                  type="checkbox"
                  className="mt-xl"
                  label={confirmCheckbox}
                  checked={confirmCheck}
                  onChange={() => setConfirmCheck(!confirmCheck)}
                  qa-element={`${qaElement}-confirm`}
                />
              )}
            </Modal.Body>
          )
          }
        <Modal.Footer className="border-top-0">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmShow(false)}
            qa-element={`${qaElement.replace(/-\d+$/, '')}-cancel`}
          >
            {cancelTitle || t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={confirmVariant || variant}
            loading={loading}
            disabled={!!confirmCheckbox && !confirmCheck}
            onClick={handleConfirm}
            qa-element={`${qaElement.replace(/-\d+$/, '')}-submit`}
          >
            {confirmTitle || t('common.yes')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ButtonWithConfirm;
